import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import ViewProfileButton from '../../components/candidates/ViewProfileButton';
import CandidateSearchCardSkeleton from '../../components/candidate-search/CandidateSearchCardSkeleton';
import LoadingState from './components/LoadingState';
import { useAuth } from '../../hooks/useAuth';
import supabase from '../../utils/supabase.js';
import { candidateService } from '../../services/supabaseService';
import { calculateSkillsMatch, getAllSkills, fetchAllSkillsFromDatabase, processSkills, getSafeSkillName } from '../../utils/skillsUtils';
import { formatArrayForDisplay } from '../../utils/arrayNormalization';

// Constants for better performance
const CANDIDATES_PER_PAGE = 12;
const DEBOUNCE_DELAY = 300;

// Default skill options (will be replaced with dynamic ones)
const DEFAULT_SKILL_OPTIONS = getAllSkills();

// Experience levels as a constant
const EXPERIENCE_LEVELS = [
  { value: '', label: 'Any Experience' },
  { value: 'entry', label: 'Entry Level (0-2 years)' },
  { value: 'intermediate', label: 'Intermediate (2-5 years)' },
  { value: 'senior', label: 'Senior (5+ years)' }
];

// Availability options as a constant
const AVAILABILITY_OPTIONS = [
  { value: '', label: 'Any Availability' },
  { value: 'full-time', label: 'Full-time' },
  { value: 'part-time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' }
];

// Debounce hook for search optimization
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Public Contact Button Component
const PublicContactButton = ({ vaName }) => {
  return (
    <Link to="/login-register">
      <Button
        variant="primary"
        size="sm"
        iconName="MessageCircle"
        className="w-full"
      >
        Contact
      </Button>
    </Link>
  );
};

// Memoized CandidateCard component for public users
const PublicCandidateCard = React.memo(({ candidate, viewMode }) => {
  const navigate = useNavigate();
  
  // Handle navigation to VA profile
  const handleViewProfile = async () => {
    if (!candidate?.id) {
      console.error('VA ID is required to view profile');
      return;
    }
    
    // Check if we have public_username
    if (candidate.public_username) {
      navigate(`/va/${candidate.public_username}`);
      return;
    }
    
    // If public_username is not available, fetch it first
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('public_username')
        .eq('id', candidate.id)
        .single();
      
      if (error) throw error;
      
      if (data && data.public_username) {
        navigate(`/va/${data.public_username}`);
      } else {
        // As a last resort, redirect to a page that will handle the lookup
        navigate(`/va-lookup/${candidate.id}`);
      }
    } catch (err) {
      console.error('Error fetching public username:', err);
      // As a last resort, redirect to a page that will handle the lookup
      navigate(`/va-lookup/${candidate.id}`);
    }
  };
  // Calculate skills match with error handling
  const skillsMatchScore = useMemo(() => {
    try {
      if (!candidate?.skills || !Array.isArray(candidate.skills)) return 0;
      // For public view, we'll just show a basic score based on number of skills
      return Math.min(100, candidate.skills.length * 15);
    } catch {
      return 0;
    }
  }, [candidate?.skills]);

  const displayName = candidate?.full_name || candidate?.name || 'Unknown VA';
  const displayTitle = candidate?.title || candidate?.job_title || 'Virtual Assistant';
  const displayLocation = candidate?.location || 'Not specified';
  const displayRate = candidate?.hourly_rate ? `$${candidate.hourly_rate}/hr` : 'Rate not specified';
  const displayRating = candidate?.average_rating || 0;
  const displayExperience = candidate?.experience_years || 0;
  const displayBio = candidate?.bio || 'No bio available';
  const displayAvailability = candidate?.availability || 'Not specified';
  const displayPreferredProjectTypes = useMemo(() => {
    return formatArrayForDisplay(candidate?.preferred_project_types, ', ', 'Not specified');
  }, [candidate?.preferred_project_types]);

  // Derived flags for video intro and AI interview
  const hasVideoIntro = !!candidate?.video_introduction_url;
  const hasAiInterview = Array.isArray(candidate?.ai_interview_sessions) &&
    candidate.ai_interview_sessions.some(s => s.status === 'completed' && s.is_public);

  const ProfilePicture = React.useMemo(() => (
    <div className="w-16 h-16 rounded-full bg-secondary-200 flex items-center justify-center overflow-hidden">
      {candidate?.profile_picture ? (
        <img 
          src={candidate.profile_picture} 
          alt={displayName}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <Icon name="User" size={24} className="text-secondary-500" />
      )}
    </div>
  ), [candidate?.profile_picture, displayName]);

  const SkillBadges = React.useMemo(() => {
    if (!candidate?.skills || !Array.isArray(candidate.skills)) return null;
    
    return candidate.skills.slice(0, 3).map((skill, index) => (
      <span 
        key={`${candidate.id}-skill-${index}`}
        className="px-2 py-1 bg-primary-50 text-primary-700 text-xs rounded-md"
      >
        {getSafeSkillName(skill)}
      </span>
    ));
  }, [candidate?.skills, candidate.id]);

  if (viewMode === 'list') {
    return (
      <div 
        className="bg-surface rounded-lg border border-border p-4 hover:border-primary hover:shadow-md transition-all duration-200 cursor-pointer"
        onClick={handleViewProfile}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {ProfilePicture}
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold text-text-primary">{displayName}</h3>
                {candidate?.identity_verification_status === 'verified' && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold"
                    title="Identity Verified"
                  >
                    <Icon name="ShieldCheck" size={11} />
                    ID Verified
                  </span>
                )}
              </div>
              <p className="text-text-secondary">{displayTitle}</p>
              <div className="flex items-center space-x-4 mt-1 text-sm text-text-muted">
                <span className="flex items-center">
                  <Icon name="MapPin" size={14} className="mr-1" />
                  {displayLocation}
                </span>
                <span className="flex items-center">
                  <Icon name="DollarSign" size={14} className="mr-1" />
                  {displayRate}
                </span>
                <span className="flex items-center">
                  <Icon name="Clock" size={14} className="mr-1" />
                  {displayExperience} years
                </span>
                {displayRating > 0 && (
                  <span className="flex items-center">
                    <Icon name="Star" size={14} className="mr-1 text-warning" />
                    {displayRating.toFixed(1)}
                  </span>
                )}
              </div>
              <div className="flex items-center mt-1 text-sm text-text-muted">
                <span className="flex items-center">
                  <Icon name="Calendar" size={14} className="mr-1" />
                  Status: {displayAvailability}
                </span>
              </div>
              <div className="flex items-center mt-1 text-sm text-text-muted">
                <span className="flex items-center">
                  <Icon name="Briefcase" size={14} className="mr-1" />
                  Projects: {displayPreferredProjectTypes}
                </span>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {SkillBadges}
                {candidate?.skills && candidate.skills.length > 3 && (
                  <span className="px-2 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-md">
                    +{candidate.skills.length - 3} more
                  </span>
                )}
              </div>
              {(hasVideoIntro || hasAiInterview) && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {hasVideoIntro && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-medium">
                      <Icon name="Video" size={11} />
                      Video Intro
                    </span>
                  )}
                  {hasAiInterview && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded-full text-xs font-medium">
                      <Icon name="Bot" size={11} />
                      AI Interviewed
                    </span>
                  )}
                </div>
              )}
              <div className="mt-2 text-sm text-text-secondary line-clamp-2">
                {displayBio}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <PublicContactButton vaName={displayName} />
          </div>
        </div>
      </div>
    );
  }

  // Grid view (default)
  return (
    <div 
      className="bg-surface rounded-lg border border-border p-6 hover:border-primary hover:shadow-lg transition-all duration-200 cursor-pointer"
      onClick={handleViewProfile}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          {ProfilePicture}
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-text-primary">{displayName}</h3>
              {candidate?.identity_verification_status === 'verified' && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold"
                    title="Identity Verified"
                  >
                    <Icon name="ShieldCheck" size={11} />
                    ID Verified
                  </span>
                )}
            </div>
            <p className="text-text-secondary">{displayTitle}</p>
          </div>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center text-sm text-text-muted">
          <Icon name="MapPin" size={14} className="mr-2" />
          <span>{displayLocation}</span>
        </div>
        <div className="flex items-center text-sm text-text-muted">
          <Icon name="DollarSign" size={14} className="mr-2" />
          <span>{displayRate}</span>
        </div>
        <div className="flex items-center text-sm text-text-muted">
          <Icon name="Clock" size={14} className="mr-2" />
          <span>{displayExperience} years experience</span>
        </div>
        {displayRating > 0 && (
          <div className="flex items-center text-sm text-text-muted">
            <Icon name="Star" size={14} className="mr-2 text-warning" />
            <span>{displayRating.toFixed(1)} rating</span>
          </div>
        )}
        <div className="flex items-center text-sm text-text-muted">
          <Icon name="Calendar" size={14} className="mr-2" />
          <span>Status: {displayAvailability}</span>
        </div>
        <div className="flex items-center text-sm text-text-muted">
          <Icon name="Briefcase" size={14} className="mr-2" />
          <span>Projects: {displayPreferredProjectTypes}</span>
        </div>
      </div>

      <div className="mb-4">
        <h4 className="text-sm font-medium text-text-primary mb-2">About Me:</h4>
        <p className="text-sm text-text-secondary line-clamp-3">
          {displayBio}
        </p>
      </div>

      <div className="mb-4">
        <h4 className="text-sm font-medium text-text-primary mb-2">Top Skills</h4>
        <div className="flex flex-wrap gap-1">
          {SkillBadges}
          {candidate?.skills && candidate.skills.length > 3 && (
            <span className="px-2 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-md">
              +{candidate.skills.length - 3} more
            </span>
          )}
        </div>
      </div>

      {(hasVideoIntro || hasAiInterview) && (
        <div className="flex flex-wrap gap-2 mb-3">
          {hasVideoIntro && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-medium">
              <Icon name="Video" size={11} />
              Video Intro
            </span>
          )}
          {hasAiInterview && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded-full text-xs font-medium">
              <Icon name="Bot" size={11} />
              AI Interviewed
            </span>
          )}
        </div>
      )}

      {/* <div className="flex space-x-2">
        <PublicContactButton vaName={displayName} />
      </div> */}
    </div>
  );
});

PublicCandidateCard.displayName = 'PublicCandidateCard';

const PublicCandidateSearch = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [skillSearchQuery, setSkillSearchQuery] = useState('');
  
  // Redirect logged-in users to /candidate-search
  useEffect(() => {
    if (user) {
      navigate('/candidate-search', { replace: true });
    }
  }, [user, navigate]);
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchError, setSearchError] = useState(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [filters, setFilters] = useState({
    skills: searchParams.get('skills')?.split(',').filter(Boolean) || [],
    experience_level: searchParams.get('experience') || '',
    hourly_rate_min: searchParams.get('min_rate') || '',
    hourly_rate_max: searchParams.get('max_rate') || '',
    location: searchParams.get('location') || '',
    availability: searchParams.get('availability') || '',
    rating_min: searchParams.get('min_rating') || ''
  });
  
  // Results state
  const [candidates, setCandidates] = useState([]);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  
  // UI state
  const [viewMode, setViewMode] = useState(
    localStorage.getItem('candidateViewMode') || 'grid'
  );
  const [showFilters, setShowFilters] = useState(true);

  // Refs for performance
  const abortControllerRef = useRef(null);

  // Debounced search query for better performance
  const debouncedSearchQuery = useDebounce(searchQuery, DEBOUNCE_DELAY);

  // Memoized options
  const [skillOptions, setSkillOptions] = useState(DEFAULT_SKILL_OPTIONS);
  const experienceLevels = useMemo(() => EXPERIENCE_LEVELS, []);
  const availabilityOptions = useMemo(() => AVAILABILITY_OPTIONS, []);

  // Forward declaration of loadCandidates (defined below)
  const loadCandidatesRef = useRef(null);
  const totalPages = useMemo(() => {
    return Math.ceil(totalCandidates / CANDIDATES_PER_PAGE) || 1;
  }, [totalCandidates]);

  // Calculate which page numbers to show in pagination
  const calculatePageNumbers = useCallback(() => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages if there are few
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Show a subset of pages with current page in the middle when possible
      let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
      let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      
      // Adjust if we're near the end
      if (endPage - startPage + 1 < maxPagesToShow) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    }
    
    return pageNumbers;
  }, [currentPage, totalPages]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    if (newPage === currentPage || newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    
    // Use the current loadCandidates reference from the ref
    loadCandidatesRef.current(false, newPage);
    
    // Scroll to top of results
    const resultsContainer = document.querySelector('.lg\\:w-3\\/4');
    if (resultsContainer) {
      window.scrollTo({
        top: resultsContainer.offsetTop - 20,
        behavior: 'smooth'
      });
    }
  }, [currentPage, totalPages]);

  // Load candidates with optimized caching and error handling
  const loadCandidates = useCallback(async (isInitialLoad = false, page = 1) => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller if supported
    if (typeof window !== 'undefined' && window.AbortController) {
      abortControllerRef.current = new window.AbortController();
    }
    
    setLoading(true);
    setSearchError(null);

    try {
      // Load with standard pagination
      const searchFilters = {
        query: debouncedSearchQuery.trim(),
        skills: filters.skills,
        experience_level: filters.experience_level,
        hourly_rate_min: filters.hourly_rate_min ? Number(filters.hourly_rate_min) : null,
        hourly_rate_max: filters.hourly_rate_max ? Number(filters.hourly_rate_max) : null,
        location: filters.location,
        availability: filters.availability,
        rating_min: filters.rating_min ? Number(filters.rating_min) : null,
        limit: CANDIDATES_PER_PAGE,
        offset: (page - 1) * CANDIDATES_PER_PAGE,
        sort_by: 'updated_at',
        sort_order: 'desc',
        ...(abortControllerRef.current && { signal: abortControllerRef.current.signal })
      };
      
      const result = await candidateService.searchCandidates(searchFilters);
      
      if (!result) {
        throw new Error('Invalid response from search service');
      }
      
      const { candidates: searchResults = [], total = 0, hasMore = false, error } = result;
      
      if (error) throw new Error(error);

      // Update state
      if (page === 1) {
        setCandidates(searchResults);
      } else {
        setCandidates(prev => [...prev, ...searchResults]);
      }
      
      setTotalCandidates(total);
      setHasNextPage(hasMore);
      
      return searchResults;
    } catch (error) {
      if (error.name !== 'AbortError') {
        setSearchError(error.message || 'Failed to load candidates. Please try again.');
        setCandidates([]);
        setTotalCandidates(0);
        setHasNextPage(false);
      }
      return [];
    } finally {
      setLoading(false);
      if (isInitialLoad) setInitialLoading(false);
    }
  }, [debouncedSearchQuery, filters]);
  
  // Store loadCandidates in ref for access from other functions
  useEffect(() => {
    loadCandidatesRef.current = loadCandidates;
  }, [loadCandidates]);

  // Handle search with debouncing
  const handleSearch = useCallback(async (resetPage = true) => {
    if (resetPage) {
      setCurrentPage(1);
    }
    
    // Update URL params
    const params = new URLSearchParams();
    if (debouncedSearchQuery) params.set('q', debouncedSearchQuery);
    if (filters.skills.length > 0) params.set('skills', filters.skills.join(','));
    if (filters.experience_level) params.set('experience', filters.experience_level);
    if (filters.hourly_rate_min) params.set('min_rate', filters.hourly_rate_min);
    if (filters.hourly_rate_max) params.set('max_rate', filters.hourly_rate_max);
    if (filters.location) params.set('location', filters.location);
    if (filters.availability) params.set('availability', filters.availability);
    if (filters.rating_min) params.set('min_rating', filters.rating_min);
    if (currentPage > 1) params.set('page', currentPage.toString());
    
    setSearchParams(params);
    
    if (loadCandidatesRef.current) {
      await loadCandidatesRef.current(false, resetPage ? 1 : currentPage);
    }
  }, [currentPage, debouncedSearchQuery, filters, setSearchParams]);

  // Filter handlers
  const handleSkillToggle = useCallback((skill) => {
    // Process input skill in case it contains commas
    const processedSkills = processSkills(skill);
    
    setFilters(prev => {
      // Get current skills
      const currentSkills = [...prev.skills];
      
      // For each processed skill
      processedSkills.forEach(processedSkill => {
        if (currentSkills.includes(processedSkill)) {
          // Remove the skill if it's already included
          const index = currentSkills.indexOf(processedSkill);
          if (index !== -1) {
            currentSkills.splice(index, 1);
          }
        } else {
          // Add the skill if it's not included
          currentSkills.push(processedSkill);
        }
      });
      
      return {
        ...prev,
        skills: currentSkills
      };
    });
  }, []);

  const handleFilterChange = useCallback((filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({
      skills: [],
      experience_level: '',
      hourly_rate_min: '',
      hourly_rate_max: '',
      location: '',
      availability: '',
      rating_min: ''
    });
    setSearchQuery('');
    setSearchParams({});
  }, [setSearchParams]);

  // Initialize component
  useEffect(() => {
    const initializeComponent = async () => {
      setInitialLoading(true);
      
      try {
        // Load all skills including custom ones
        const allSkills = await fetchAllSkillsFromDatabase();
        setSkillOptions(allSkills);
        
        // Check if there's a page parameter in the URL
        const pageParam = searchParams.get('page');
        if (pageParam) {
          const parsedPage = parseInt(pageParam, 10);
          if (!isNaN(parsedPage) && parsedPage > 0) {
            setCurrentPage(parsedPage);
          }
        }
        
        // Always load candidates, even if there are no search parameters
        // The supabaseService will handle showing recently active candidates when no filters are applied
        if (loadCandidatesRef.current) {
          await loadCandidatesRef.current(true, currentPage);
        }
      } catch (error) {
        console.error("Error initializing component:", error);
        setSearchError('Failed to load initial data');
        setInitialLoading(false);
      }
    };

    // Using a timeout to ensure ref is set before trying to use it
    const timer = setTimeout(() => {
      initializeComponent();
    }, 0);
    
    return () => clearTimeout(timer);
  }, [searchParams, currentPage]);

  // Auto-search when debounced query or filters change
  useEffect(() => {
    if (!initialLoading) {
      // Always perform the search, regardless of whether filters are applied
      handleSearch(true);
    }
  }, [debouncedSearchQuery, filters, handleSearch, initialLoading]);

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('candidateViewMode', viewMode);
  }, [viewMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="h-9 w-72 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-3" />
            <div className="h-5 w-96 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-4" />
            {/* CTA Banner Skeleton */}
            <div className="bg-surface border border-border rounded-lg p-4 mb-6 flex items-center justify-between">
              <div>
                <div className="h-5 w-48 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2" />
                <div className="h-4 w-72 bg-gradient-to-r from-gray-200 to-gray-300 rounded" />
              </div>
              <div className="h-10 w-36 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg flex-shrink-0" />
            </div>
          </div>

          {/* Two-column layout skeleton */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar Skeleton */}
            <div className="lg:w-1/4 lg:min-w-[280px]">
              <div className="bg-surface rounded-lg border border-border p-4 animate-pulse">
                {/* Search input */}
                <div className="h-10 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-4" />
                <div className="border-t border-border pt-4 mb-4 flex justify-between">
                  <div className="h-4 w-16 bg-gradient-to-r from-gray-200 to-gray-300 rounded" />
                  <div className="h-4 w-14 bg-gradient-to-r from-gray-200 to-gray-300 rounded" />
                </div>
                {/* Skills filter */}
                <div className="mb-4">
                  <div className="h-4 w-10 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2" />
                  <div className="h-9 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-3" />
                  <div className="flex flex-wrap gap-2">
                    {[60, 80, 52, 70, 64, 88, 56, 72].map((w, i) => (
                      <div key={i} className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full" style={{ width: w }} />
                    ))}
                  </div>
                </div>
                {/* Experience filter */}
                <div className="mb-4">
                  <div className="h-4 w-28 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2" />
                  <div className="h-9 bg-gradient-to-r from-gray-200 to-gray-300 rounded" />
                </div>
                {/* Rate range */}
                <div className="mb-4">
                  <div className="h-4 w-36 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2" />
                  <div className="grid grid-cols-2 gap-2">
                    <div className="h-9 bg-gradient-to-r from-gray-200 to-gray-300 rounded" />
                    <div className="h-9 bg-gradient-to-r from-gray-200 to-gray-300 rounded" />
                  </div>
                </div>
                {/* Location */}
                <div className="mb-4">
                  <div className="h-4 w-16 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2" />
                  <div className="h-9 bg-gradient-to-r from-gray-200 to-gray-300 rounded" />
                </div>
                {/* Availability */}
                <div className="mb-4">
                  <div className="h-4 w-20 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2" />
                  <div className="h-9 bg-gradient-to-r from-gray-200 to-gray-300 rounded" />
                </div>
              </div>
            </div>

            {/* Main content skeleton */}
            <div className="lg:w-3/4 lg:flex-1">
              {/* Results controls bar */}
              <div className="bg-surface rounded-lg border border-border p-4 mb-4 animate-pulse flex items-center justify-between">
                <div className="h-4 w-32 bg-gradient-to-r from-gray-200 to-gray-300 rounded" />
                <div className="flex items-center space-x-2">
                  <div className="h-6 w-px bg-border mx-2" />
                  <div className="h-8 w-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded" />
                  <div className="h-8 w-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded" />
                </div>
              </div>
              {/* Card grid */}
              <LoadingState viewMode={viewMode} count={6} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Build a dynamic description based on active filters
  const seoTitle = filters.skills.length > 0
    ? `Hire ${filters.skills.slice(0, 2).join(' & ')} Virtual Assistants | VA Sprouts`
    : 'Find Talented Virtual Assistants | VA Sprouts';
  const seoDescription = filters.skills.length > 0
    ? `Browse verified ${filters.skills.slice(0, 3).join(', ')} virtual assistants on VA Sprouts. View profiles, rates, and availability. Sign up to hire today.`
    : 'Browse skilled virtual assistants on VA Sprouts. Filter by skills, experience, location, and availability to find the perfect VA for your business.';
  const canonicalPath = typeof window !== 'undefined' ? window.location.href.split('?')[0] : 'https://vasprouts.com/find-virtual-assistants';

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={canonicalPath} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={canonicalPath} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-primary mb-2">
            Find Talented Virtual Assistants
          </h1>
          <p className="text-text-secondary mb-4">
            Discover skilled VAs that match your requirements. Sign up to contact and hire them.
          </p>
          
          {/* Call to Action Banner */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-primary-900 mb-1">
                  Ready to hire top VAs?
                </h3>
                <p className="text-primary-700">
                  Create your employer account to contact candidates and post jobs.
                </p>
              </div>
              <Link to="/login-register">
                <Button variant="primary" size="lg">
                  Get Started Free
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Two-column layout: Filters sidebar + Candidates list */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filters Sidebar */}
          <div className="lg:w-1/4 lg:min-w-[280px]">
            <div className="bg-surface rounded-lg border border-border p-4 sticky top-20">
              {/* Search Input */}
              <div className="mb-4">
                <div className="relative">
                  <Icon 
                    name="Search" 
                    size={20} 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" 
                  />
                  <Input
                    type="text"
                    placeholder="Search by name, skills..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4"
                  />
                </div>
              </div>

              <div className="border-t border-border pt-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-text-primary">Filters</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    className="text-sm"
                  >
                    Clear All
                  </Button>
                </div>
              </div>

              {/* Skills Filter */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Skills
                </label>
                <div className="mb-3">
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Search skills..."
                      value={skillSearchQuery}
                      onChange={(e) => setSkillSearchQuery(e.target.value.toLowerCase())}
                      className="pl-10 pr-4"
                      fullWidth
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Icon name="search" size={18} className="text-gray-400" />
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto">
                  {skillOptions
                    .filter((skill) => skill.toLowerCase().includes(skillSearchQuery))
                    .map((skill) => (
                      <button
                        key={skill}
                        onClick={() => handleSkillToggle(skill)}
                        className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                          filters.skills.includes(skill)
                            ? 'bg-primary-100 border-primary-300 text-primary-700'
                            : 'bg-surface border-border text-text-secondary hover:border-primary-300'
                        }`}
                      >
                        {skill}
                      </button>
                    ))}
                </div>
                {skillSearchQuery && skillOptions.filter((skill) => skill.toLowerCase().includes(skillSearchQuery)).length === 0 && (
                  <p className="text-xs text-gray-500 mt-2">No skills match "{skillSearchQuery}"</p>
                )}
              </div>

              {/* Experience Level */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Experience Level
                </label>
                <select
                  value={filters.experience_level}
                  onChange={(e) => handleFilterChange('experience_level', e.target.value)}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
                >
                  {experienceLevels.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Hourly Rate Range */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Hourly Rate Range ($)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Input
                      type="number"
                      placeholder="Min"
                      value={filters.hourly_rate_min}
                      onChange={(e) => handleFilterChange('hourly_rate_min', e.target.value)}
                      min="0"
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      placeholder="Max"
                      value={filters.hourly_rate_max}
                      onChange={(e) => handleFilterChange('hourly_rate_max', e.target.value)}
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Location
                </label>
                <Input
                  type="text"
                  placeholder="e.g., Philippines, Remote"
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                />
              </div>

              {/* Availability */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Availability
                </label>
                <select
                  value={filters.availability}
                  onChange={(e) => handleFilterChange('availability', e.target.value)}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
                >
                  {availabilityOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Minimum Rating */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Minimum Rating
                </label>
                <select
                  value={filters.rating_min}
                  onChange={(e) => handleFilterChange('rating_min', e.target.value)}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
                >
                  <option value="">Any Rating</option>
                  <option value="4">4+ Stars</option>
                  <option value="4.5">4.5+ Stars</option>
                  <option value="5">5 Stars</option>
                </select>
              </div>
            </div>
          </div>

          {/* Candidates List */}
          <div className="lg:w-3/4 lg:flex-1">
            {/* Results Controls */}
            <div className="bg-surface rounded-lg border border-border p-4 mb-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <span className="text-text-secondary">
                    {totalCandidates} {totalCandidates === 1 ? 'candidate' : 'candidates'} found
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="border-r border-border h-6 mr-2"></div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant={viewMode === 'grid' ? 'primary' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('grid')}
                      iconName="Grid3X3"
                    />
                    <Button
                      variant={viewMode === 'list' ? 'primary' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('list')}
                      iconName="List"
                    />
                  </div>
                </div>
              </div>
            </div>


            {/* Results */}
        {initialLoading ? (
          <LoadingState viewMode={viewMode} count={6} />
        ) : candidates.length > 0 ? (
          <>
            <div className={`grid gap-6 ${
              viewMode === 'grid' 
                ? 'grid-cols-1 md:grid-cols-2' 
                : 'grid-cols-1'
            }`}>
              {candidates.map((candidate) => (
                <PublicCandidateCard
                  key={candidate.id}
                  candidate={candidate}
                  viewMode={viewMode}
                />
              ))}
            </div>
          </>
        ) : !loading ? (
          <div className="text-center py-12">
            <Icon name="Users" size={48} className="text-text-muted mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-text-primary mb-2">
              No candidates found
            </h3>
            <p className="text-text-secondary mb-4">
              Try adjusting your search criteria or clearing filters to see more results.
            </p>
            <Button variant="outline" onClick={clearAllFilters}>
              Clear All Filters
            </Button>
          </div>
        ) : null}

        {/* Pagination Controls */}
        {candidates.length > 0 && (
          <div className="flex justify-center items-center mt-8 space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
              disabled={loading || currentPage === 1}
              iconName="ChevronLeft"
            />
            
            {/* Page number buttons */}
            {calculatePageNumbers().map(pageNum => (
              <Button
                key={pageNum}
                variant={pageNum === currentPage ? "primary" : "outline"}
                size="sm"
                onClick={() => handlePageChange(pageNum)}
                disabled={loading}
                className={pageNum === currentPage ? "" : "hover:bg-primary-50"}
              >
                {pageNum}
              </Button>
            ))}
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => hasNextPage && handlePageChange(currentPage + 1)}
              disabled={loading || !hasNextPage}
              iconName="ChevronRight"
            />
          </div>
        )}

        {/* Loading Indicator */}
        {loading && candidates.length > 0 && (
          <div className="mt-8">
            <LoadingState viewMode={viewMode} count={3} />
          </div>
        )}
          </div>
        </div>

        {/* Error State */}
        {searchError && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <Icon name="AlertCircle" size={20} className="text-error mr-2" />
              <span className="text-error">{searchError}</span>
            </div>
          </div>
        )}

        {/* Bottom CTA */}
        {candidates.length > 0 && (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 mt-8 text-center">
            <h3 className="text-xl font-semibold text-primary-900 mb-2">
              Ready to hire these talented VAs?
            </h3>
            <p className="text-primary-700 mb-4">
              Join thousands of employers who have found their perfect virtual assistants.
            </p>
            <Link to="/login-register">
              <Button variant="primary" size="lg">
                Start Hiring Today
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicCandidateSearch;
